'use client';

import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowLeft, Search, Loader2 } from 'lucide-react';
import { Header } from '../../landing/components/Header';
import { AuthModal } from '../../auth/components/AuthModal';
import { EventCard } from './EventsCard';
import { Event, EventWithRelations } from '../../../services/EventService';
import { slugify } from '../../../utils/slugify';
import redirectTo from '../../../utils/redirect';

interface SearchResultsProps {
  searchQuery?: string;
}

export const SearchResults = ({ searchQuery }: SearchResultsProps) => {
  const [searchParams] = useSearchParams();
  const queryFromParams = searchParams.get('q') || searchQuery || '';
  
  const [searchValue, setSearchValue] = useState(queryFromParams);
  const [events, setEvents] = useState<EventWithRelations []>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const performSearch = async (query: string) => {
    if (!query.trim()) {
      setEvents([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { data, error: searchError } = await Event.searchEvents(query, {
        is_public: true
      });

      if (searchError) {
        setError('Error searching. Please try again.');
        console.error('Search error:', searchError);
        return;
      }

      setEvents(data || []);
      
    } catch (error) {
      console.error('Search error:', error);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoBack = () => {
    window.history.back();
  };

  const handleSearch = () => {
    performSearch(searchValue);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleEventClick = (event: EventWithRelations) => {
    // Redirigir a la página del evento usando slugify
    const eventSlug = slugify(event.title);
    redirectTo(`/event/${eventSlug}`);
  };

  // Realizar búsqueda inicial cuando el componente se monta o cambia la query
  useEffect(() => {
    if (queryFromParams) {
      performSearch(queryFromParams);
    }
  }, [queryFromParams]);

  const hasResults = events.length > 0;
  const hasSearched = searchValue.trim() !== '';

  return (
    <div className="min-h-screen bg-gray-50">
      <Header onSignInClick={() => setShowAuthModal(true)}  />
      
      <div className="pt-20 mt-6 px-4 max-w-6xl mx-auto">
        {/* Search Bar */}
        <div className="flex items-center gap-4 mb-8">
          <div className="flex-1 flex items-center bg-white rounded-lg border border-gray-200 px-4 py-3">
            <Search className="w-5 h-5 text-gray-400 mr-3" />
            <input
              type="text"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyPress={handleKeyPress}
              className="flex-1 outline-none text-gray-900"
              placeholder="Buscar eventos..."
              disabled={isLoading}
            />
            <button
              onClick={handleSearch}
              disabled={isLoading}
              className="bg-primary text-white px-6 py-2 rounded-md hover:bg-accent/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Buscando...
                </>
              ) : (
                'Buscar'
              )}
            </button>
          </div>
        </div>

        {/* Go Back Button */}
        <button 
          onClick={handleGoBack}
          className="flex items-center gap-2 text-purple-600 hover:text-purple-700 mb-6 text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Go back
        </button>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* No Results Message */}
        {hasSearched && !isLoading && !hasResults && !error && (
          <div className="text-center py-12">
            <div className="text-gray-500 text-lg mb-2">No encontramos eventos</div>
            <p className="text-gray-400">Proba con otras palabras claves</p>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-gray-400" />
            <p className="text-gray-500">Buscando...</p>
          </div>
        )}

        {/* Results */}
        {!isLoading && hasResults && (
          <>
            {/* Events Section */}
            {events.length > 0 && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Events ({events.length})
                </h2>
                <div className="space-y-4">
                  <table className="w-full table-fixed border-separate border-spacing-y-3">
                    <tbody>
                      {events.map((event) => (
                        <EventCard
                          key={event.id}
                          organizerId={event.hosts?.find((host: any) => host.is_main_host)?.user_id || ''}
                          organizer={event.hosts?.find((host: any) => host.is_main_host)?.name || 'Unknown'}
                          organizerAvatar={event.hosts?.find((host: any) => host.is_main_host)?.avatar_url || ''}
                          id={event.id}
                          title={event.title}
                          description={event.short_description || event.description || ''}
                          date={event.start_date ?? ''}
                          time={event.start_time ?? ''}
                          location={event.location || 'TBD'}
                          category={event.category}
                          image={event.image_url || "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=80&h=80&fit=crop"}
                          parent_event_id={(event as any).parent_event_id}
                          onClick={() => {
                            const slug = slugify(event.title);
                            redirectTo(`/event/${slug}`);
                          }}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* Initial State - Show when no search has been performed */}
        {!hasSearched && !isLoading && (
          <div className="text-center py-12">
            <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Buscá eventos del ecosistema</h3>
            <p className="text-gray-500">Ingresá palabras clave para encontrar actividades</p>
          </div>
        )}
      </div>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        skipRedirect={true}
        onSuccess={() => {
          setShowAuthModal(false)
          // Optionally refresh the page or update user state
          window.location.reload()
        }}
      />
    </div>
  );
};